import React, { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Camera, Wind, Heart, ArrowLeft } from 'lucide-react'

interface Gallery {
  title: string
  subtitle: string
  category: string
  location: string
  thumbnail: string
  keywords: string[]
  images: Array<{
    id: string
    filename: string
    thumbnail: string
    fullsize: string
    keywords?: string[]
  }>
}

export const WeddingDressesPage: React.FC = () => {
  const { galleryId } = useParams<{ galleryId?: string }>()
  const [gallery, setGallery] = useState<Gallery | null>(null)
  const [loading, setLoading] = useState(true)
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [currentImageIndex, setCurrentImageIndex] = useState(0)

  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const response = await fetch('/data/galleries.json')
        const data = await response.json()
        
        // Check if this is accessed through gallery route
        if (galleryId === 'wedding-dresses-in-motion') {
          setGallery(data['wedding-dresses-in-motion'])
        } else {
          // Show general wedding dresses page
          setGallery(null)
        }
      } catch (error) {
        console.error('Error fetching gallery:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchGallery()
  }, [galleryId])

  const openLightbox = (index: number) => {
    setCurrentImageIndex(index)
    setLightboxOpen(true)
  }

  const closeLightbox = () => {
    setLightboxOpen(false)
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-900"></div>
      </div>
    )
  }

  // If gallery is found and accessed through gallery route, show gallery view
  if (gallery) {
    const currentImage = gallery.images[currentImageIndex]

    return (
      <div className="min-h-screen">
        {/* Gallery Header */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="max-w-7xl mx-auto">
            <Link 
              to="/portfolio" 
              className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-6 transition-colors duration-200"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Portfolio
            </Link>
            
            <div className="text-center">
              <h1 className="text-4xl md:text-5xl font-light text-gray-900 mb-4 tracking-wide">
                {gallery.title}
              </h1>
              <p className="text-xl text-gray-600 mb-2">{gallery.subtitle}</p>
              <p className="text-sm text-gray-500 uppercase tracking-wide">
                {gallery.category} • {gallery.location}
              </p>
              <div className="mt-6 text-sm text-gray-500">
                {gallery.images.length} {gallery.images.length === 1 ? 'image' : 'images'}
              </div>
            </div>
          </div>
        </section>

        {/* Thumbnail Grid */}
        <section className="py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {gallery.images.map((image, index) => (
                <button
                  key={image.id}
                  onClick={() => openLightbox(index)}
                  className="aspect-square overflow-hidden bg-gray-100 hover:shadow-lg transition-all duration-300 group"
                >
                  <img
                    src={image.thumbnail}
                    alt={`${gallery.title} - Image ${index + 1}`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Lightbox */}
        {lightboxOpen && currentImage && (
          <div className="fixed inset-0 bg-black z-50 flex items-center justify-center">
            <button
              onClick={closeLightbox}
              className="absolute top-4 right-4 p-2 bg-black/50 text-white rounded-full hover:bg-black/70 transition-colors duration-200"
            >
              ✕
            </button>

            <div className="w-full h-full flex items-center justify-center p-16">
              <img
                src={currentImage.fullsize}
                alt={`${gallery.title} - Image ${currentImageIndex + 1}`}
                className="max-w-full max-h-full object-contain"
              />
            </div>

            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-white text-sm">
              {currentImageIndex + 1} / {gallery.images.length}
            </div>
          </div>
        )}
      </div>
    )
  }

  // Default wedding dresses page view
  return (
    <div className="min-h-screen">
      {/* Header */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-light text-gray-900 mb-6 tracking-wide">
            Wedding Dresses in Motion
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed">
            Capturing the ethereal beauty and flowing elegance of your wedding gown
          </p>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white p-12 shadow-sm border border-gray-100">
            <div className="text-center mb-8">
              <Wind className="w-8 h-8 text-gray-600 mx-auto mb-4" />
              <h2 className="text-3xl font-light text-gray-900 mb-6">The Art of Motion</h2>
            </div>
            
            <p className="text-lg text-gray-700 leading-relaxed mb-6 text-center">
              There's something magical about capturing a wedding dress in motion. The way fabric flows 
              and dances in the wind, the graceful movement that adds poetry to a moment – these are the 
              details that transform a photograph into fine art.
            </p>
            
            <p className="text-gray-600 leading-relaxed text-center">
              Our specialized approach to photographing wedding dresses in motion combines technical 
              expertise with artistic vision, creating images that celebrate both the craftsmanship 
              of your gown and the beauty of movement itself.
            </p>
          </div>
        </div>
      </section>

      {/* Techniques Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-light text-gray-900 mb-12 text-center">Our Approach</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-6 bg-white shadow-sm">
              <Camera className="w-12 h-12 text-gray-600 mx-auto mb-6" />
              <h3 className="text-xl font-light text-gray-900 mb-4">Technical Mastery</h3>
              <p className="text-gray-600 leading-relaxed">
                Using specialized techniques to freeze motion at the perfect moment, 
                capturing every detail of fabric flow and movement.
              </p>
            </div>
            
            <div className="text-center p-6 bg-white shadow-sm">
              <Wind className="w-12 h-12 text-gray-600 mx-auto mb-6" />
              <h3 className="text-xl font-light text-gray-900 mb-4">Natural Elements</h3>
              <p className="text-gray-600 leading-relaxed">
                Working with natural wind and movement to create authentic, 
                graceful motion that enhances the dress's inherent beauty.
              </p>
            </div>
            
            <div className="text-center p-6 bg-white shadow-sm">
              <Heart className="w-12 h-12 text-gray-600 mx-auto mb-6" />
              <h3 className="text-xl font-light text-gray-900 mb-4">Artistic Vision</h3>
              <p className="text-gray-600 leading-relaxed">
                Every shot is composed with an artist's eye, creating dramatic, 
                romantic images that celebrate the gown as wearable art.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* When We Capture */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-light text-gray-900 mb-8 text-center">
            Perfect Moments for Motion Photography
          </h2>
          
          <div className="space-y-8">
            <div className="flex flex-col md:flex-row gap-6 items-center">
              <div className="md:w-1/3">
                <h3 className="text-xl font-light text-gray-900 mb-2">Bridal Portraits</h3>
                <p className="text-gray-600">
                  Dedicated time for capturing your dress in motion, creating stunning 
                  artistic portraits that showcase the gown's movement.
                </p>
              </div>
              <div className="md:w-2/3 bg-gray-100 h-48 flex items-center justify-center">
                <p className="text-gray-500 italic">Sample bridal motion portrait</p>
              </div>
            </div>

            <div className="flex flex-col md:flex-row-reverse gap-6 items-center">
              <div className="md:w-1/3">
                <h3 className="text-xl font-light text-gray-900 mb-2">First Look Moments</h3>
                <p className="text-gray-600">
                  The emotional first look provides natural opportunities for 
                  capturing genuine movement and the dress's response to the moment.
                </p>
              </div>
              <div className="md:w-2/3 bg-gray-100 h-48 flex items-center justify-center">
                <p className="text-gray-500 italic">First look motion capture</p>
              </div>
            </div>

            <div className="flex flex-col md:flex-row gap-6 items-center">
              <div className="md:w-1/3">
                <h3 className="text-xl font-light text-gray-900 mb-2">Reception Dancing</h3>
                <p className="text-gray-600">
                  The natural movement of dancing creates perfect opportunities 
                  for capturing the dress in its most joyful, celebratory motion.
                </p>
              </div>
              <div className="md:w-2/3 bg-gray-100 h-48 flex items-center justify-center">
                <p className="text-gray-500 italic">Dance floor motion photography</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-light text-gray-900 mb-6">
            Showcase Your Gown's Beauty
          </h2>
          <p className="text-gray-600 mb-8 leading-relaxed">
            Let us capture the ethereal beauty of your wedding dress in motion, 
            creating artistic images that celebrate both the craftsmanship of your gown 
            and the joy of your special day.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/quote"
              className="px-8 py-3 bg-gray-900 text-white font-light tracking-wide hover:bg-gray-800 transition-colors duration-300"
            >
              Request a Quote
            </Link>
            <Link
              to="/portfolio"
              className="px-8 py-3 border border-gray-900 text-gray-900 font-light tracking-wide hover:bg-gray-900 hover:text-white transition-colors duration-300"
            >
              View Portfolio
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}
